import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'timeConversion',
})
export class TimeConversionPipe implements PipeTransform {
  // Time unit constants in seconds
  private readonly SECOND = 1;
  private readonly MINUTE = 60;
  private readonly HOUR = this.MINUTE * 60;
  private readonly DAY = this.HOUR * 24;
  private readonly MONTH = this.DAY * 30;
  private readonly YEAR = this.DAY * 365;

  transform(seconds: number): string {
    if (seconds === null || seconds === undefined || isNaN(seconds)) {
      return 'Invalid input';
    }

    // Convert to absolute value to handle negative seconds
    const absoluteSeconds = Math.abs(seconds);

    // Define all time units in descending order
    const timeUnits = [
      { value: Math.floor(absoluteSeconds / this.YEAR), label: 'year' },
      {
        value: Math.floor((absoluteSeconds % this.YEAR) / this.MONTH),
        label: 'month',
      },
      {
        value: Math.floor((absoluteSeconds % this.MONTH) / this.DAY),
        label: 'day',
      },
      {
        value: Math.floor((absoluteSeconds % this.DAY) / this.HOUR),
        label: 'hour',
      },
      {
        value: Math.floor((absoluteSeconds % this.HOUR) / this.MINUTE),
        label: 'minute',
      },
      { value: Math.floor(absoluteSeconds % this.MINUTE), label: 'second' },
    ];

    // Filter out leading and trailing zeros
    const significantUnits = this.trimInsignificantUnits(timeUnits);

    if (significantUnits.length === 0) {
      return '0 seconds';
    }

    // Format the result
    return significantUnits
      .map((unit) => `${unit.value} ${this.pluralize(unit.label, unit.value)}`)
      .join(' ');
  }

  /**
   * Remove leading and trailing zero values
   */
  private trimInsignificantUnits(
    units: Array<{ value: number; label: string }>
  ): Array<{ value: number; label: string }> {
    // Find the first non-zero unit from the beginning
    const firstSignificantIndex = units.findIndex((unit) => unit.value > 0);

    // If all values are zero, return empty array
    if (firstSignificantIndex === -1) {
      return [];
    }

    // Find the last non-zero unit from the end
    const unitsWithValues = units.filter((unit) => unit.value > 0);
    if (unitsWithValues.length === 0) {
      return [];
    }

    const lastSignificantLabel =
      unitsWithValues[unitsWithValues.length - 1].label;
    const lastSignificantIndex = units.findIndex(
      (unit) => unit.label === lastSignificantLabel
    );

    // Return only the significant range
    return units.slice(firstSignificantIndex, lastSignificantIndex + 1);
  }

  /**
   * Pluralize a label based on count
   */
  private pluralize(label: string, count: number): string {
    return count === 1 ? label : `${label}s`;
  }
}
